import { useState } from "react";
import { motion } from "framer-motion";
import { BUILDS, PLANS, FEATURES, computeTotals } from "@/components/build/pickerData";
import { OptionCard } from "@/components/build/OptionCard";
import { FeatureCard } from "@/components/build/FeatureCard";
import { SummaryBar } from "@/components/build/SummaryBar";
import { SendListDialog } from "@/components/build/SendListDialog";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
};

const Step = ({ n, title, hint, children }) => (
  <motion.div {...fadeUp} transition={{ duration: 0.7 }} className="mt-16 first:mt-0">
    <div className="flex items-baseline gap-4">
      <span className="font-display text-4xl text-jade/30">{n}</span>
      <h3 className="font-display text-2xl font-medium text-ink sm:text-3xl">
        {title} {hint && <span className="text-base font-normal text-ink2/60">({hint})</span>}
      </h3>
    </div>
    <div className="mt-6">{children}</div>
  </motion.div>
);

const BuildYourOwn = () => {
  const [build, setBuild] = useState("standard");
  const [plan, setPlan] = useState("essential");
  const [features, setFeatures] = useState([]);
  const [open, setOpen] = useState(false);
  const totals = computeTotals(build, plan, features);
  const toggle = (id) => setFeatures((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));

  return (
    <section id="build" className="relative bg-paper2/60 py-24 lg:py-32" data-testid="build-your-own-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.p {...fadeUp} transition={{ duration: 0.6 }} className="text-xs font-medium uppercase tracking-[0.3em] text-jade">
          Build your own
        </motion.p>
        <motion.h2 {...fadeUp} transition={{ duration: 0.7, delay: 0.05 }} className="mt-5 max-w-3xl font-display text-4xl font-medium leading-tight text-ink sm:text-5xl">
          Pick what you need. <em className="text-jade">See it add up.</em>
        </motion.h2>
        <motion.p {...fadeUp} transition={{ duration: 0.7, delay: 0.1 }} className="mt-5 max-w-xl text-base leading-relaxed text-ink2">
          Every choice is reflected in your monthly care plan, so there are no surprises later.
        </motion.p>

        <div className="mt-16">
          <Step n="1" title="Choose your build">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {BUILDS.map((b) => (
                <OptionCard key={b.id} option={b} selected={build === b.id} onSelect={() => setBuild(b.id)} testId={`build-card-${b.id}`} />
              ))}
            </div>
          </Step>
          <Step n="2" title="Choose your care plan" hint="monthly">
            <div className="grid gap-4 md:grid-cols-3">
              {PLANS.map((p) => (
                <OptionCard key={p.id} option={p} suffix="/mo" selected={plan === p.id} onSelect={() => setPlan(p.id)} testId={`plan-card-${p.id}`} />
              ))}
            </div>
          </Step>
          <Step n="3" title="Add features" hint="monthly costs roll into your retainer">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              {FEATURES.map((f) => (
                <FeatureCard key={f.id} feature={f} selected={features.includes(f.id)} onToggle={() => toggle(f.id)} />
              ))}
            </div>
          </Step>
        </div>

        <SummaryBar totals={totals} onSend={() => setOpen(true)} />
      </div>
      <SendListDialog open={open} onOpenChange={setOpen} totals={totals} />
    </section>
  );
};

export default BuildYourOwn;
